import React from 'react';
import { createRoot } from 'react-dom/client';
import './styles/main.scss';
import VisitRequestForm from './components/forms/VisitRequestForm';
import WWOOFerVisitRequestForm from './components/forms/WWOOFerVisitRequestForm';
import BookingDashboard from './components/booking/BookingDashboard';
import UserAvailabilityCalendar from './components/availability/UserAvailabilityCalendar';

const MOUNTED_ATTR = 'data-gw-mounted';

// Mount a React tree into a container exactly once. Safe to call repeatedly,
// which matters for the Elementor editor where widgets are re-rendered via AJAX.
function mountContainer(container, render) {
  if (!container || container.getAttribute(MOUNTED_ATTR) === 'true') {
    return;
  }
  container.setAttribute(MOUNTED_ATTR, 'true');
  createRoot(container).render(render());
}

function mountVisitRequestForms() {
  document.querySelectorAll('#wwoof-visit-request-form').forEach((container) => {
    mountContainer(container, () => (
      <VisitRequestForm
        wwooferId={parseInt(container.dataset.wwooferId)}
        hostId={parseInt(container.dataset.hostId)}
        threadId={parseInt(container.dataset.threadId)}
      />
    ));
  });

  document.querySelectorAll('#wwoof-wwoofer-visit-request-form').forEach((container) => {
    mountContainer(container, () => (
      <WWOOFerVisitRequestForm
        hostId={parseInt(container.dataset.hostId)}
        wwooferId={parseInt(container.dataset.wwooferId)}
        threadId={parseInt(container.dataset.threadId)}
      />
    ));
  });
}

function mountBookingDashboards() {
  document.querySelectorAll('#wwoof-booking-dashboard').forEach((container) => {
    mountContainer(container, () => (
      <BookingDashboard
        userId={parseInt(container.dataset.userId)}
        view={container.dataset.view}
      />
    ));
  });
}

function mountAvailabilityCalendars() {
  document.querySelectorAll('#wwoof-user-availability-calendar').forEach((container) => {
    let bookingDates = [];
    if (container.dataset.bookingDates) {
      try {
        bookingDates = JSON.parse(container.dataset.bookingDates);
      } catch (e) {
        bookingDates = [];
      }
    }
    mountContainer(container, () => (
      <UserAvailabilityCalendar
        userId={parseInt(container.dataset.userId)}
        canEdit={container.dataset.canEdit === 'true'}
        bookingDates={bookingDates}
      />
    ));
  });
}

function mountComponents() {
  mountVisitRequestForms();
  mountBookingDashboards();
  mountAvailabilityCalendars();
}

function debounce(fn, wait) {
  let timer = null;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(fn, wait);
  };
}

// Wait for React to be available (keeping compatibility with WordPress setup)
function waitForReact(callback) {
  if (typeof React !== 'undefined' && typeof createRoot !== 'undefined') {
    callback();
  } else {
    setTimeout(() => waitForReact(callback), 100);
  }
}

waitForReact(function () {
  const onReady = () => {
    mountComponents();

    // Elementor re-renders widgets via AJAX in the editor preview, so hook into
    // its frontend lifecycle and observe DOM insertions as a fallback.
    if (window.elementorFrontend && window.elementorFrontend.hooks) {
      window.elementorFrontend.hooks.addAction('frontend/element_ready/global', mountComponents);
      window.elementorFrontend.hooks.addAction(
        'frontend/element_ready/gw_visit_request_form.default',
        mountComponents
      );
    }

    if (window.MutationObserver) {
      const observer = new MutationObserver(debounce(mountComponents, 100));
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
});
